import axios from "axios";
import { authService } from "./authService";

const env = process.env.REACT_APP_ENVIRONMENT;
const apiBaseUrl =
  env === "LOCAL"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PRODUCTION;

const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: { "Content-Type": "application/json" },
  timeout: 90000,
});

apiClient.interceptors.request.use(async (config) => {
  const token = await authService.acquireToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// EquipmentView (nested) → flat UI object used by equipment components
const mapEquipmentViewToUI = ({ id, created_at, equipment }) => ({
  id,
  created_at,
  name: equipment.name ?? "",
  serial_number: equipment.serial_number ?? "",
  assigned_to: equipment.assigned_to ?? null,
  assigned_to_name: equipment.assigned_to_name ?? "",
  status: equipment.status ?? "",
});

// Flat UI object → EquipmentEntry payload expected by the backend
const mapUIToPayload = (flat) => ({
  name: flat.name === "" || flat.name == null ? null : flat.name,
  serial_number:
    flat.serial_number === "" || flat.serial_number == null
      ? null
      : flat.serial_number,
  assigned_to: flat.assigned_to == null ? null : flat.assigned_to,
  status: flat.status === "" || flat.status == null ? null : flat.status,
});

class EquipmentService {
  /** List equipment with optional search, status filter, and pagination. */
  getEquipment = async (search = "", limit = 50, skip = 0, filters = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    });
    if (search) params.append("search", search);
    if (filters.status?.length) {
      filters.status.forEach((s) => params.append("status", s));
    }
    if (filters.assigned_to != null) {
      params.append("assigned_to", String(filters.assigned_to));
    }
    const { data } = await apiClient.get(`/api/equipment?${params}`);
    return {
      ...data,
      items: data.items.map(mapEquipmentViewToUI),
    };
  };

  /** Fetch a single equipment item by id. Returns flat UI object. */
  getEquipmentItem = async (id) => {
    const { data } = await apiClient.get(`/api/equipment/${id}`);
    return mapEquipmentViewToUI(data);
  };

  /** Create an equipment item. payload = flat UI fields object. */
  createEquipment = async (payload) => {
    try {
      const { data } = await apiClient.post(
        "/api/equipment",
        mapUIToPayload(payload),
      );
      return mapEquipmentViewToUI(data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create equipment";
      throw new Error(msg);
    }
  };

  /** Update an equipment item. payload = flat UI fields object. */
  updateEquipment = async (id, payload) => {
    try {
      const { data } = await apiClient.put(
        `/api/equipment/${id}`,
        mapUIToPayload(payload),
      );
      return mapEquipmentViewToUI(data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to update equipment";
      throw new Error(msg);
    }
  };

  /** Delete an equipment item by id. */
  deleteEquipment = async (id) => {
    const res = await apiClient.delete(`/api/equipment/${id}`);
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  /** Export equipment to an Excel blob. */
  exportEquipment = async ({ search, status, assigned_to } = {}) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status?.length) status.forEach((s) => params.append("status", s));
    if (assigned_to != null) params.set("assigned_to", String(assigned_to));

    const res = await apiClient.get(`/api/equipment/export?${params}`, {
      responseType: "blob",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      validateStatus: (s) => s >= 200 && s < 300,
    });
    return res.data; // Blob
  };
}

export const equipmentService = new EquipmentService();
